import { useOnboardingActions } from "./useOnboardingActions";
import { getOnboardingPathFromAction } from "../utils/getOnboardingPath";

export type UseOnboardingGateArgs = {
  /** Program ID to check onboarding for */
  programId: number | undefined;
  /** Current user */
  user: { id: number } | null;
};

export type UseOnboardingGateResult = {
  /** Whether to redirect to onboarding */
  shouldRedirect: boolean;
  /** Path to redirect to (if shouldRedirect is true) */
  redirectPath: string | null;
  /** Whether onboarding status is loading */
  isLoading: boolean;
  /** Whether all onboardings are complete */
  isComplete: boolean;
};

/**
 * Hook that determines if a user should be redirected to onboarding.
 *
 * Use this in gate components that protect routes requiring completed onboarding.
 *
 * @example
 * ```tsx
 * const { shouldRedirect, redirectPath, isLoading } = useOnboardingGate({
 *   programId,
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 * if (shouldRedirect && redirectPath) return <Navigate to={redirectPath} />;
 * return <>{children}</>;
 * ```
 */
export function useOnboardingGate({
  programId,
  user,
}: UseOnboardingGateArgs): UseOnboardingGateResult {
  const { allOnboardingsComplete, onboardingFlows, isLoading } =
    useOnboardingActions({
      programId: programId ?? 0,
      user,
    });

  // No program ID means we can't check onboarding, allow through
  if (!programId) {
    return {
      shouldRedirect: false,
      redirectPath: null,
      isLoading: false,
      isComplete: true,
    };
  }

  // Still loading, don't redirect yet
  if (isLoading) {
    return {
      shouldRedirect: false,
      redirectPath: null,
      isLoading: true,
      isComplete: false,
    };
  }

  // Onboarding not complete, redirect to the first incomplete flow's onboarding page
  if (!allOnboardingsComplete) {
    const firstIncompleteFlow = onboardingFlows.find(
      (flow) => !flow.allRequiredCompleted
    );

    // If no incomplete flow is found (e.g. all steps are complete but the
    // parent action status is temporarily stale), allow through rather than
    // hardcoding a redirect to participant onboarding.
    if (!firstIncompleteFlow) {
      return {
        shouldRedirect: false,
        redirectPath: null,
        isLoading: false,
        isComplete: false,
      };
    }

    const redirectPath = getOnboardingPathFromAction(
      firstIncompleteFlow.onboardingAction,
      programId
    );

    return {
      shouldRedirect: true,
      redirectPath,
      isLoading: false,
      isComplete: false,
    };
  }

  // Onboarding complete, allow through
  return {
    shouldRedirect: false,
    redirectPath: null,
    isLoading: false,
    isComplete: true,
  };
}

